import api from '../services/api';

export async function getRoles(queryParams) { // queryParams can be URLSearchParams or undefined
  try {
    // Updated to use ApiRoles controller
    let endpoint = '/api/roles';
    if (queryParams && queryParams.toString()) {
      endpoint += `?${queryParams.toString()}`;
    }
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    console.error('Error fetching roles:', error);
    throw error;
  }
}

export async function getRoleById(id) {
  try {
    // Updated to use ApiRoles controller - call edit endpoint for full data
    const response = await api.get(`/api/roles/edit/${id}`); 
    return response.data;
  } catch (error) {
    console.error(`Error fetching role ${id}:`, error);
    throw error;
  }
}

export async function createRole(roleData) {
  try {
    // Updated to use ApiRoles controller
    const response = await api.post('/api/roles/save', roleData); 
    return response.data;
  } catch (error) {
    console.error('Error creating role:', error);
    throw error;
  }
}

export async function updateRole(id, roleData) {
  try {
    // Updated to use ApiRoles controller
    // Include the role ID in the request body
    const requestData = {
      ...roleData,
      role_id: id
    };
    const response = await api.post('/api/roles/update', requestData); 
    return response.data;
  } catch (error) {
    console.error(`Error updating role ${id}:`, error);
    throw error;
  }
}

export async function deleteRole(id) {
  try {
    // Updated to use ApiRoles controller
    const response = await api.delete(`/api/roles/${id}`); 
    return response.data;
  } catch (error) {
    console.error(`Error deleting role ${id}:`, error);
    throw error;
  }
}

// getRoleName might not be needed if getRoleById returns the full object
// export async function getRoleName(id) { ... }

// Auth related functions
export async function authenticate(credentials) {
  try {
    const response = await api.post('/api/auth/login', credentials);
    return response.data;
  } catch (error) {
    console.error('Error during authentication:', error);
    throw error;
  }
}

export async function logout() {
  try {
    const response = await api.post('/api/auth/logout');
    return response.data;
  } catch (error) {
    console.error('Error during logout:', error);
    throw error;
  }
}

export async function fetchUserData() {
  try {
    const response = await api.get('/api/auth/user');
    return response.data;
  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
}

// Password reset and update functions
export async function requestPasswordReset(email) {
  try {
    const response = await api.post('/api/auth/password/reset', { email });
    return response.data;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    throw error;
  }
}

export async function resetPassword(token, newPassword) {
  try {
    const response = await api.post('/api/auth/password/reset/confirm', { token, newPassword });
    return response.data;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
}

export async function updatePassword(currentPassword, newPassword) {
  try {
    const response = await api.put('/api/auth/password/update', { currentPassword, newPassword });
    return response.data;
  } catch (error) {
    console.error('Error updating password:', error);
    throw error;
  }
}

// User data update function
export async function updateUserData(userData) {
  try {
    const response = await api.put('/api/auth/user', userData);
    return response.data;
  } catch (error) {
    console.error('Error updating user data:', error);
    throw error;
  }
}

// Auth callback handling (e.g., after social login)
export async function handleAuthCallback(url) {
  const urlParams = new URLSearchParams(url.split('?')[1]);
  const authToken = urlParams.get('authToken');
  const userDataStr = urlParams.get('userData');
  
  // console.log('Auth callback parameters:', {
  //   authToken: authToken ? 'present' : 'missing',
  //   userDataStr: userDataStr ? 'present' : 'missing'
  // });
  
  if (authToken && userDataStr) {
    try {
      // Decode and parse user data
      const userData = JSON.parse(decodeURIComponent(userDataStr));
      
      // Store token and user data in local storage
      localStorage.setItem('token', authToken);
      localStorage.setItem('userData', JSON.stringify(userData));
      
      // Optionally, set axios default authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      
      return userData;
    } catch (error) {
      console.error('Error handling auth callback:', error);
      throw error;
    }
  } else {
    throw new Error('Invalid auth callback parameters');
  }
}

// Axios response interceptor to handle 401 errors globally
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      const originalRequestUrl = error.config.url;
      // Don't redirect on 401 for session check or login endpoints
      if (originalRequestUrl.endsWith('/api/check_session') || originalRequestUrl.endsWith('/api/auth/login')) {
        return Promise.reject(error); // Reject the promise to allow the caller to handle it
      }
      
      // For all other 401s, redirect to login
      // Optionally, you can also show a notification or message to the user
      // window.location.href = '/login'; // Redirect to login page
    }
    return Promise.reject(error);
  }
);